import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaPlus, FaList, FaThLarge, FaBuilding } from "react-icons/fa";
import {
  getCompanies,
  setFilters,
  setPage,
  setLimit,
  setViewMode,
} from "../../features/companies/companiesSlice";
import { AppDispatch, RootState } from "../../store";
import CompanyFormModal from "./components/CompanyFormModal";
import CompaniesFilter from "./components/CompaniesFilter";
import CompaniesList from "./components/CompaniesList";
import CompaniesGrid from "./components/CompaniesGrid";
import Pagination from "../../components/common/Pagination";
import ErrorAlert from "../../components/common/ErrorAlert";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";

interface CompaniesPageProps {
  isNew?: boolean;
  isDetail?: boolean;
  isEdit?: boolean;
}

const CompaniesPage: React.FC<CompaniesPageProps> = ({
  isNew = false,
  isDetail = false,
  isEdit = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const {
    companies,
    isLoading,
    error,
    totalCount,
    page,
    limit,
    filters,
    viewMode,
  } = useSelector((state: RootState) => state.companies);

  const [showCreateModal, setShowCreateModal] = useState(isNew);
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialCompanyType = query.get("type") === "agency" ? "agency" : query.get("type") === "client" ? "client" : query.get("type") === "single" ? "single" : undefined;

  // Eğer route isNew ise modali aç; değilse query param gelirse de aç
  useEffect(() => {
    if (initialCompanyType && !showCreateModal) {
      setShowCreateModal(true);
    }
  }, [initialCompanyType]);
  const [companyTypeFilter, setCompanyTypeFilter] = useState<string>("all");

  // Şirketleri yükle
  useEffect(() => {
    const updatedFilters = { ...filters };
    if (companyTypeFilter !== "all") {
      updatedFilters.company_type = companyTypeFilter;
    } else {
      delete updatedFilters.company_type;
    }

    dispatch(setFilters(updatedFilters));
  }, [companyTypeFilter]);

  // Filtreler, sayfa veya limit değiştiğinde şirketleri yeniden yükle
  useEffect(() => {
    dispatch(getCompanies({ ...filters, page, limit }));
  }, [dispatch, filters, page, limit]);

  // Görünüm modunu değiştir
  const handleViewModeChange = (mode: "list" | "grid") => {
    dispatch(setViewMode(mode));
  };

  // Sayfa değişikliği
  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  // Sayfa başına öğe sayısı değişikliği
  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1));
  };

  // Şirket türü filtreleme
  const handleCompanyTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyTypeFilter(e.target.value);
    dispatch(setPage(1));
  };

  return (
    <Container fluid className="py-4 companies-page">
      {/* Compact Modern Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <Icon icon={FaBuilding} />
            </div>
            <div className="header-text">
              <h1 className="page-title">{t("companies")}</h1>
              <p className="page-subtitle">
                {totalCount} {t("total")} •{" "}
                {companies.filter((c) => c.is_active).length} {t("active")}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            className="add-button"
            onClick={() => setShowCreateModal(true)}
          >
            <Icon icon={FaPlus} className="me-2" />
            {t("new_company")}
          </Button>
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      {/* Modern Filter and Controls Card */}
      <Card className="filter-card">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            {/* Sol taraf - Filtreler */}
            <div className="d-flex flex-wrap align-items-center gap-3">
              <Form.Select
                size="sm"
                value={companyTypeFilter}
                onChange={handleCompanyTypeChange}
                className="filter-select"
                style={{ width: "160px" }}
              >
                <option value="all">{t("all_companies")}</option>
                <option value="client">{t("clients")}</option>
                <option value="agency">{t("agencies")}</option>
              </Form.Select>

              {/* Status badges */}
              <div className="d-flex gap-2">
                <Badge className="status-badge danger-badge">
                  {companies.filter((c) => c.is_active).length} {t("active")}
                </Badge>
                <Badge className="status-badge danger-badge">
                  {companies.filter((c) => !c.is_active).length} {t("inactive")}
                </Badge>
              </div>
            </div>

            {/* Sağ taraf - View controls */}
            <div className="d-flex align-items-center gap-3">
              <div className="view-toggle">
                <Button
                  variant={viewMode === "list" ? "primary" : "outline-primary"}
                  size="sm"
                  className="view-btn"
                  onClick={() => handleViewModeChange("list")}
                >
                  <Icon icon={FaList} />
                </Button>
                <Button
                  variant={viewMode === "grid" ? "primary" : "outline-primary"}
                  size="sm"
                  className="view-btn"
                  onClick={() => handleViewModeChange("grid")}
                >
                  <Icon icon={FaThLarge} />
                </Button>
              </div>

              <Form.Select
                size="sm"
                value={limit}
                onChange={handleLimitChange}
                className="limit-select"
                style={{ width: "80px" }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Advanced Filters */}
      <CompaniesFilter />

      {/* Content Area */}
      <Card className="content-card">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">{t("loading")}...</p>
            </div>
          ) : companies.length > 0 ? (
            <div className="p-4">
              {viewMode === "list" ? (
                <CompaniesList companies={companies} />
              ) : (
                <CompaniesGrid companies={companies} />
              )}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <Icon icon={FaBuilding} size="3rem" />
              </div>
              <h5 className="empty-title">{t("no_companies_found")}</h5>
              <p className="empty-text">
                {t("try_adjusting_filters_or_add_new_company")}
              </p>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                className="empty-button"
              >
                <Icon icon={FaPlus} className="me-2" />
                {t("add_first_company")}
              </Button>
            </div>
          )}
        </Card.Body>

        {totalCount > 0 && (
          <Card.Footer className="pagination-footer">
            <div className="d-flex justify-content-between align-items-center">
              <div className="pagination-info">
                <strong>{totalCount}</strong> {t("companies")} {t("total")}
              </div>
              <Pagination
                currentPage={page}
                totalCount={totalCount}
                pageSize={limit}
                onPageChange={handlePageChange}
              />
            </div>
          </Card.Footer>
        )}
      </Card>

      <CompanyFormModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        initialCompanyType={initialCompanyType}
      />

      <style>{`
        /* Root Variables for Theme Support */
        .companies-page {
          --bg-primary: #ffffff;
          --bg-secondary: #f8fafc;
          --bg-tertiary: #f1f5f9;
          --text-primary: #0f172a;
          --text-secondary: #475569;
          --text-muted: #64748b;
          --border-color: #e2e8f0;
          --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.1);
          --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.07);
          --accent-primary: #3b82f6;
          --accent-secondary: #8b5cf6;
          --success-bg: #dcfce7;
          --success-text: #166534;
          --warning-bg: #fef3c7;
          --warning-text: #92400e;
        }

        /* Dark Theme */
        [data-bs-theme="dark"] .companies-page {
          --bg-primary: #1e293b;
          --bg-secondary: #334155;
          --bg-tertiary: #475569;
          --text-primary: #f8fafc;
          --text-secondary: #cbd5e1;
          --text-muted: #94a3b8;
          --border-color: #475569;
          --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.3);
          --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.2);
          --success-bg: #064e3b;
          --success-text: #6ee7b7;
          --warning-bg: #451a03;
          --warning-text: #fbbf24;
        }

        /* Header Styles */
        .page-header {
          margin-bottom: 24px;
          padding: 20px 24px;
          background: var(--bg-primary);
          border-radius: 16px;
          box-shadow: var(--shadow-light);
          border: 1px solid var(--border-color);
          transition: all 0.3s ease;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .header-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
          transition: all 0.3s ease;
        }

        .header-icon:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
        }

        .header-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
          line-height: 1.2;
        }

        .page-subtitle {
          font-size: 14px;
          color: var(--text-muted);
          margin: 0;
          font-weight: 500;
        }

        .add-button {
          padding: 10px 20px;
          font-size: 14px;
          font-weight: 600;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          white-space: nowrap;
          color: white;
        }

        .add-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
          color: white;
        }

        .add-button:active {
          transform: translateY(0);
        }

        /* Card Styles */
        .filter-card,
        .content-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          box-shadow: var(--shadow-light);
          margin-bottom: 24px;
          border-radius: 16px;
          transition: all 0.3s ease;
        }

        /* Filter Elements */
        .filter-select,
        .limit-select {
          border: 1px solid var(--border-color);
          background: var(--bg-secondary);
          color: var(--text-primary);
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .filter-select:focus,
        .limit-select:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
          background: var(--bg-primary);
        }

        /* Status Badges */
        .status-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          border: none;
        }

        .success-badge {
          background: var(--success-bg);
          color: var(--success-text);
        }

        .secondary-badge {
          background: var(--warning-bg);
          color: var(--warning-text);
        }

        /* View Toggle */
        .view-toggle {
          display: flex;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: var(--shadow-light);
        }

        .view-btn {
          border-radius: 0;
          border: 1px solid var(--border-color);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          transition: all 0.2s ease;
        }

        .view-btn:first-child {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
        }

        .view-btn:last-child {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          border-left: none;
        }

        .view-btn.btn-primary {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: white;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: var(--bg-tertiary);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          color: var(--text-muted);
        }

        .empty-title {
          color: var(--text-secondary);
          margin-bottom: 12px;
        }

        .empty-text {
          color: var(--text-muted);
          margin-bottom: 24px;
        }

        .empty-button {
          background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
          border: none;
          border-radius: 10px;
          padding: 12px 24px;
          font-weight: 600;
          color: white;
          transition: all 0.2s ease;
        }

        .empty-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
          color: white;
        }

        /* Pagination Footer */
        .pagination-footer {
          background: var(--bg-secondary);
          border-top: 1px solid var(--border-color);
          border-radius: 0 0 16px 16px;
          padding: 20px 24px;
        }

        .pagination-info {
          color: var(--text-muted);
          font-weight: 500;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .page-header {
            padding: 16px 20px;
            margin-bottom: 20px;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .header-info {
            gap: 12px;
          }

          .header-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .page-title {
            font-size: 20px;
          }

          .page-subtitle {
            font-size: 13px;
          }

          .add-button {
            padding: 8px 16px;
            font-size: 13px;
            align-self: stretch;
            justify-content: center;
          }

          .filter-card .card-body {
            padding: 16px;
          }

          .view-toggle {
            order: -1;
            width: 100%;
          }

          .view-btn {
            flex: 1;
          }
        }

        @media (max-width: 576px) {
          .header-content {
            gap: 12px;
          }

          .page-title {
            font-size: 18px;
          }

          .page-subtitle {
            font-size: 12px;
          }

          .empty-state {
            padding: 40px 20px;
          }

          .empty-icon {
            width: 60px;
            height: 60px;
          }
        }

        /* Smooth Transitions */
        * {
          transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
        }
      `}</style>
    </Container>
  );
};

export default CompaniesPage;
